import { Loader2 } from "lucide-react";
import React, { useEffect, useRef } from "react";

/** Whether the element actually clips its content, i.e. can act as a bounded
 *  IntersectionObserver root. A wrapper that merely grows with its content
 *  (the default `overflow: visible`) has no such bounds. */
const clipsOverflow = (element: Element): boolean => {
  const { overflowX, overflowY } = window.getComputedStyle(element);
  return overflowX !== "visible" || overflowY !== "visible";
};

export interface InfiniteScrollProps {
  scrollRef: React.RefObject<HTMLElement | null>;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  offset?: number;
  disabled?: boolean;
  loadingDelay?: number;
  spinner?: React.ReactNode;
}

export const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  scrollRef,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  offset = 200,
  disabled = false,
  loadingDelay = 300, // debounce time
  spinner = (
    <div className="flex w-full justify-center py-4">
      <Loader2 className="h-6 w-6 animate-spin text-neutral-500" />
    </div>
  )
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled || !hasNextPage) return;

    const currentSentinel = sentinelRef.current;
    if (!currentSentinel) return;

    // Observe against the scroll container, not the viewport. A list inside a
    // dropdown or modal can sit partly (or entirely) below the viewport — with
    // `root: null` its sentinel then never intersects and the next page never
    // loads. Using the container also makes `offset` mean "this far from the
    // end of the list", which is the intent. Fall back to the viewport when the
    // ref isn't an ancestor, since a non-ancestor root never intersects at all
    // — and also when the ancestor doesn't clip its content, since such a root
    // has no bounds for the sentinel to ever leave and would report
    // intersecting immediately, paging through the whole list at once.
    const container = scrollRef?.current ?? null;
    const root =
      container &&
      container.contains(currentSentinel) &&
      clipsOverflow(container)
        ? container
        : null;

    let timeout: ReturnType<typeof setTimeout> | null = null;

    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isFetchingNextPage && timeout === null) {
          timeout = setTimeout(() => {
            timeout = null;
            fetchNextPage();
          }, loadingDelay);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      root,
      rootMargin: `0px 0px ${offset}px 0px`,
      threshold: 0
    });
    observer.observe(currentSentinel);

    return () => {
      if (timeout !== null) clearTimeout(timeout);
      observer.disconnect();
    };
  }, [
    scrollRef,
    offset,
    disabled,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    loadingDelay
  ]);

  return (
    <>
      {isFetchingNextPage && spinner}
      {/* Needs a real box: a zero-height sentinel is unreliable to observe. */}
      <div ref={sentinelRef} className="h-px w-full" />
    </>
  );
};
